import { useCallback, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import styled from 'styled-components';
import { TfiUpload } from 'react-icons/tfi';
import Button from 'react-bootstrap/Button';

import { useVideoProjectCreation } from '@contexts/VideoProjectCreation.context';
import { useToast } from '@contexts/Toast.context';
import { validateVideoFile, formatFileSize } from '@utils/video/helpers';
import Loader from '@components/loading/Loader';

const Container = styled.div`
	display: flex;
	flex-direction: column;
	gap: 1.5rem;
`;

const DropZoneContainer = styled.div`
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
	padding: 3rem 2rem;
	border: 2px dashed
		${({ $isDragActive, theme }) =>
			$isDragActive ? theme.colors.green : 'rgba(255, 255, 255, 0.3)'};
	border-radius: 0.375rem;
	background: ${({ $isDragActive }) =>
		$isDragActive ? 'rgba(25, 135, 84, 0.1)' : 'rgba(0, 0, 0, 0.2)'};
	cursor: pointer;
	transition: all 0.3s ease;

	&:hover {
		border-color: ${({ theme }) => theme.colors.green};
		background: rgba(25, 135, 84, 0.05);
	}
`;

const UploadIcon = styled(TfiUpload)`
	font-size: 3rem;
	color: ${({ theme }) => theme.colors.textAccent};
	margin-bottom: 1rem;
`;

const DropZoneText = styled.p`
	font-size: 1.125rem;
	color: ${({ theme }) => theme.colors.textAccent};
	margin: 0 0 0.5rem 0;
	text-align: center;
`;

const DropZoneHint = styled.small`
	color: rgba(255, 255, 255, 0.6);
	text-align: center;
	display: block;
`;

const FileInfoContainer = styled.div`
	padding: 1.5rem;
	background: rgba(${({ theme }) => theme.colors.borderLightRgb}, 0.1);
	border-radius: 0.375rem;
	border: 1px solid rgba(${({ theme }) => theme.colors.borderLightRgb}, 0.2);
`;

const FileInfoRow = styled.div`
	display: flex;
	justify-content: space-between;
	align-items: center;
	padding: 0.5rem 0;

	&:not(:last-child) {
		border-bottom: 1px solid rgba(255, 255, 255, 0.1);
	}
`;

const FileInfoLabel = styled.span`
	color: rgba(255, 255, 255, 0.7);
	font-size: 0.875rem;
`;

const FileInfoValue = styled.span`
	color: #ffffff;
	font-weight: 500;
`;

const RemoveButton = styled(Button)`
	margin-top: 1rem;
`;

const VideoFileSelector = () => {
	const { videoFile, setVideoFile, nextStage } = useVideoProjectCreation();
	const { addToast } = useToast();
	const [isValidating, setIsValidating] = useState(false);

	const handleAcceptedFiles = useCallback(
		async acceptedFiles => {
			const file = acceptedFiles[0];

			setIsValidating(true);

			try {
				// Validate file
				const validation = await validateVideoFile(file);

				if (validation.valid) {
					setVideoFile(file);
					addToast({
						id: 'video-file-selected',
						title: 'Video file selected',
						message: `${file.name} is ready to be configured.`,
						bg: 'success',
					});
				} else {
					addToast({
						id: 'video-validation-error',
						title: 'Video validation failed',
						message: validation.error,
						bg: 'danger',
					});
				}
			} catch (error) {
				addToast({
					id: 'video-validation-error',
					title: 'Video validation failed',
					message: error?.message || 'Unexpected error during validation.',
					bg: 'danger',
				});
			} finally {
				setIsValidating(false);
			}
		},
		[setVideoFile, addToast]
	);

	const handleFileRejection = useCallback(
		fileRejections => {
			const errorCode = fileRejections[0]?.errors[0]?.code;

			const errorMessages = {
				'too-many-files': 'Please select only one video file.',
				'file-too-large': 'Video file is too large. Maximum size is 10GB.',
				'file-invalid-type': 'Invalid file type. Only MP4 and MOV files are supported.',
			};

			addToast({
				id: `video-file-error-${errorCode}`,
				title: 'File selection error',
				message: errorMessages[errorCode] || 'Unknown error selecting file.',
				bg: 'danger',
			});
		},
		[addToast]
	);

	const { getRootProps, getInputProps, isDragActive } = useDropzone({
		onDropAccepted: handleAcceptedFiles,
		onDropRejected: handleFileRejection,
		accept: {
			'video/mp4': ['.mp4'],
			'video/quicktime': ['.mov'],
		},
		maxFiles: 1,
		maxSize: 10 * 1024 * 1024 * 1024, // 10GB
		multiple: false,
		disabled: isValidating || !!videoFile,
	});

	const handleRemoveFile = () => {
		setVideoFile(null);
	};

	if (isValidating) {
		return (
			<Container>
				<Loader />
				<p style={{ textAlign: 'center', color: 'rgba(255, 255, 255, 0.7)' }}>
					Validating video file...
				</p>
			</Container>
		);
	}

	return (
		<Container>
			{!videoFile ? (
				<DropZoneContainer {...getRootProps()} $isDragActive={isDragActive}>
					<input {...getInputProps()} />
					<UploadIcon />
					<DropZoneText>
						{isDragActive
							? 'Drop the video file here'
							: 'Drag and drop a video file here, or click to browse'}
					</DropZoneText>
					<DropZoneHint>
						Supported formats: MP4, MOV (max 10GB)
						<br />
						<small style={{ fontSize: '0.75rem', marginTop: '0.25rem', display: 'block' }}>
							Note: Video must use H.264 codec for browser compatibility
						</small>
					</DropZoneHint>
				</DropZoneContainer>
			) : (
				<FileInfoContainer>
					<h4 style={{ marginTop: 0, marginBottom: '1rem' }}>Selected Video</h4>

					<FileInfoRow>
						<FileInfoLabel>File Name:</FileInfoLabel>
						<FileInfoValue>{videoFile.name}</FileInfoValue>
					</FileInfoRow>

					<FileInfoRow>
						<FileInfoLabel>File Size:</FileInfoLabel>
						<FileInfoValue>{formatFileSize(videoFile.size)}</FileInfoValue>
					</FileInfoRow>

					<FileInfoRow>
						<FileInfoLabel>File Type:</FileInfoLabel>
						<FileInfoValue>{videoFile.type}</FileInfoValue>
					</FileInfoRow>

					<RemoveButton variant="danger" size="sm" onClick={handleRemoveFile}>
						Remove File
					</RemoveButton>
				</FileInfoContainer>
			)}
		</Container>
	);
};

export default VideoFileSelector;
